// /electionday turnout zone — district tile view.
//
// Layout: two outer columns. Districts 1–7 sit in the left column
// flush to the left edge of the page; districts 8–13 sit in the
// right column flush to the right edge. The entire middle of the
// page is empty gutter that the 2pm and 10pm rule labels extend
// into. No geographic cartogram, no shape arrangement — Gozo (13)
// is simply the last tile.
//
// Each tile-unit is a paper-coloured empty frame with three
// detached marks:
//   • Top bar  — runs the full width of the tile, sits above it
//                with a small gap. Coloured by current winner with
//                alpha scaled to margin of victory.
//   • Vertical bar — runs the full height of the tile, sits beside
//                it on the INNER side (right for left column, left
//                for right column). Fills bottom-up: yellow for
//                voted, purple for not-yet-voted.
//   • District number — sits in the gap between the tile and its
//                vertical bar, anchored to the inner-bottom corner.
//   • 2pm + 10pm rules — thin horizontal lines cutting through the
//                vertical bar at their respective turnout percent-
//                ages, extending inward as short stubs that
//                terminate in paper-coloured pill labels.
//
// Data sources (Stage 2 split):
//   • useElectionDayTurnout  → per-district 2pm + 10pm turnout pcts
//                              (the kind='turnout' payload entered
//                              via /admin/turnout)
//   • useElectionDayEditorial → per-district partisan vote counts
//                              (the kind='editorial' payload) used
//                              ONLY for the top-bar winner+margin
//                              colour. The vestigial turnout fields
//                              on DistrictEditorialResult are no
//                              longer read here.
//   • MAP_DATA.paths → real 2022 results_2022 baseline
// Click-to-open info panel below the columns is preserved verbatim.

import { useMemo, useState } from 'react';
import { MAP_DATA, type BuiltPath } from '../../lib/buildMapPaths';
import { useElectionDayEditorial } from '../../lib/useElectionDayEditorial';
import { useElectionDayTurnout } from '../../lib/useElectionDayTurnout';
import type { DistrictEditorialResult } from '../../data/electionday-editorial-types';
import type { DistrictTurnout } from '../../data/electionday-turnout-types';

interface TileReading {
  districtId: number;
  eligibleVoters: number;
  /** Where the partisan colour on the top bar comes from — 2026
   *  live (PL/PN votes entered) or the 2022 baseline. */
  source: '2022-baseline' | '2026-live';
  /** Winner currently colouring the top bar. */
  winner: 'PL' | 'PN' | 'tie';
  /** Absolute margin in percentage points (top-bar alpha scales
   *  with this — 0pp ≈ 0.25 alpha, 20pp+ → 1.0). */
  marginPp: number;
  /** Pct used for the vertical bar's yellow fill. Falls back
   *  10pm → 2pm → 2022 closing. */
  votedPct: number;
  /** 10pm rule render state — null means "no line drawn". */
  tenPm: {
    pct: number;
    label: '10pm' | "'22";
    muted: boolean;
  } | null;
  /** 2pm rule render state — null means "no line drawn" (we never
   *  invent a historical 2pm value; the line is omitted when no
   *  2026 2pm reading has been entered). */
  twoPm: { pct: number } | null;
  baseline2022Pct: number;
  live10pmPct: number | null;
  live2pmPct: number | null;
}

const LEFT_DISTRICTS = [1, 2, 3, 4, 5, 6, 7];
const RIGHT_DISTRICTS = [8, 9, 10, 11, 12, 13];

export default function TurnoutTileGrid() {
  const { data } = useElectionDayEditorial();
  const { data: turnout } = useElectionDayTurnout();
  const [selected, setSelected] = useState<number | null>(null);

  const readingsById = useMemo(() => {
    const out: Record<number, TileReading> = {};
    for (const p of MAP_DATA.paths) {
      const key = `District ${p.id}`;
      out[p.id] = computeReading(p, data.districts[key], turnout.districts[key]);
    }
    return out;
  }, [data.districts, turnout.districts]);

  const leftReadings = LEFT_DISTRICTS.map((id) => readingsById[id]).filter(
    Boolean,
  );
  const rightReadings = RIGHT_DISTRICTS.map((id) => readingsById[id]).filter(
    Boolean,
  );

  const selectedReading =
    selected !== null ? readingsById[selected] ?? null : null;
  const selectedEditorial = selectedReading
    ? data.districts[`District ${selectedReading.districtId}`]
    : undefined;

  return (
    <div className="electionday-turnout-tiles">
      <header className="electionday-turnout-tiles__head">
        <h3 className="electionday-turnout-tiles__title">
          Turnout by district
        </h3>
      </header>

      <div className="electionday-turnout-tiles__columns">
        <div className="electionday-turnout-tiles__col electionday-turnout-tiles__col--left">
          {leftReadings.map((r) => (
            <TileUnit
              key={r.districtId}
              reading={r}
              side="left"
              isOpen={selected === r.districtId}
              onClick={() =>
                setSelected((cur) =>
                  cur === r.districtId ? null : r.districtId,
                )
              }
            />
          ))}
        </div>
        <div className="electionday-turnout-tiles__col electionday-turnout-tiles__col--right">
          {rightReadings.map((r) => (
            <TileUnit
              key={r.districtId}
              reading={r}
              side="right"
              isOpen={selected === r.districtId}
              onClick={() =>
                setSelected((cur) =>
                  cur === r.districtId ? null : r.districtId,
                )
              }
            />
          ))}
        </div>
      </div>

      {selectedReading && (
        <InfoPanel
          reading={selectedReading}
          editorial={selectedEditorial}
          onClose={() => setSelected(null)}
        />
      )}

      <p className="electionday-turnout-tiles__source">
        Eligible voters + 2022 closing turnout: Electoral Commission
        via the project's 2022 results geojson. 2026 figures appear
        as the newsroom enters them. <strong>No district-level 2pm
        data exists for any historic election</strong> — the 2pm
        rule is drawn only for 2026 live readings.
      </p>
    </div>
  );
}

// ── Tile unit ─────────────────────────────────────────────────────

interface TileUnitProps {
  reading: TileReading;
  side: 'left' | 'right';
  isOpen: boolean;
  onClick: () => void;
}

function TileUnit({ reading, side, isOpen, onClick }: TileUnitProps) {
  // Top bar fill — partisan colour scaled by margin. Hex values
  // mirror the editorial result map's hardcoded reds + blues so
  // both surfaces match without introducing parallel constants.
  const topBarFill = winnerFill(reading.winner, reading.marginPp);

  // Vertical-bar yellow fraction — clamped to [0, 100].
  const votedPct = Math.max(0, Math.min(100, reading.votedPct));

  return (
    <button
      type="button"
      className={`electionday-turnout-tile electionday-turnout-tile--${side}${
        isOpen ? ' is-open' : ''
      }`}
      onClick={onClick}
      aria-pressed={isOpen}
      aria-label={`District ${reading.districtId} turnout details`}
    >
      <span
        className="electionday-turnout-tile__topbar"
        style={{ background: topBarFill }}
        aria-hidden="true"
      />

      <span className="electionday-turnout-tile__body" aria-hidden="true" />

      <span className="electionday-turnout-tile__num">
        {reading.districtId}
      </span>

      <span className="electionday-turnout-tile__vbar" aria-hidden="true">
        <span
          className="electionday-turnout-tile__vbar-fill"
          style={{ height: `${votedPct}%` }}
        />
      </span>

      {reading.tenPm && (
        <span
          className={`electionday-turnout-tile__rule electionday-turnout-tile__rule--10pm${
            reading.tenPm.muted ? ' is-muted' : ''
          }`}
          style={{ '--rule-pct': reading.tenPm.pct } as React.CSSProperties}
          aria-hidden="true"
        >
          <span className="electionday-turnout-tile__rule-line" />
          <span className="electionday-turnout-tile__rule-label">
            {reading.tenPm.label === "'22"
              ? `'22 — ${Math.round(reading.tenPm.pct)}%`
              : `10pm ${Math.round(reading.tenPm.pct)}%`}
          </span>
        </span>
      )}

      {reading.twoPm && (
        <span
          className="electionday-turnout-tile__rule electionday-turnout-tile__rule--2pm"
          style={{ '--rule-pct': reading.twoPm.pct } as React.CSSProperties}
          aria-hidden="true"
        >
          <span className="electionday-turnout-tile__rule-line" />
          <span className="electionday-turnout-tile__rule-label">
            2pm {Math.round(reading.twoPm.pct)}%
          </span>
        </span>
      )}
    </button>
  );
}

function winnerFill(winner: TileReading['winner'], marginPp: number): string {
  if (winner === 'tie') return 'rgba(160, 160, 160, 0.4)';
  // alpha 0.25 at 0pp margin → 1.0 at 20pp+
  const alpha = Math.max(0.25, Math.min(1, 0.25 + (marginPp / 20) * 0.75));
  // Match the editorial result map's existing colours verbatim —
  // see EditorialDistrictMap.tsx fillFor(): PL = 255,19,73 / PN =
  // 25,54,160.
  const base = winner === 'PL' ? '255, 19, 73' : '25, 54, 160';
  return `rgba(${base}, ${alpha.toFixed(2)})`;
}

// ── Reading computation ───────────────────────────────────────────

function computeReading(
  path: BuiltPath,
  ed: DistrictEditorialResult | undefined,
  tn: DistrictTurnout | undefined,
): TileReading {
  const baseline = path.properties.results_2022;
  const baselinePct = baseline.turnout_pct;

  // Top bar: 2026 live if we have a usable PL/PN vote split, else
  // 2022 baseline. The condition mirrors EditorialDistrictMap's
  // liveFrom() so the two surfaces flip in lockstep.
  let source: '2022-baseline' | '2026-live' = '2022-baseline';
  let winner: 'PL' | 'PN' | 'tie' = baseline.winner === 'PL' ? 'PL' : 'PN';
  let marginPp = Math.abs(baseline.margin_pp ?? 0);
  if (
    ed &&
    ed.pl_votes !== null &&
    ed.pn_votes !== null &&
    ed.total_valid !== null &&
    ed.total_valid > 0
  ) {
    source = '2026-live';
    const plPct = (ed.pl_votes / ed.total_valid) * 100;
    const pnPct = (ed.pn_votes / ed.total_valid) * 100;
    marginPp = Math.abs(plPct - pnPct);
    winner =
      ed.pl_votes > ed.pn_votes ? 'PL' : ed.pn_votes > ed.pl_votes ? 'PN' : 'tie';
  }

  const live10pm = tn?.pct_10pm ?? null;
  const live2pm = tn?.pct_2pm ?? null;

  // Vertical-bar fill: 10pm → 2pm → 2022 closing.
  const votedPct = live10pm ?? live2pm ?? baselinePct;

  // 10pm rule:
  //   • live 10pm set → "10pm XX%", normal
  //   • no live turnout data at all (10pm null AND 2pm null) →
  //     "'22 — XX%", muted, drawn at the 2022 closing position
  //   • some live data but no 10pm yet → no 10pm rule (we don't
  //     pretend the 2022 baseline is current when the editor has
  //     already entered an in-progress 2pm)
  let tenPm: TileReading['tenPm'] = null;
  if (live10pm !== null) {
    tenPm = { pct: live10pm, label: '10pm', muted: false };
  } else if (live2pm === null) {
    tenPm = { pct: baselinePct, label: "'22", muted: true };
  }

  // 2pm rule: drawn ONLY when the editor has entered a 2026 2pm
  // reading. No district-level 2pm history exists for any year —
  // never invented.
  const twoPm: TileReading['twoPm'] =
    live2pm !== null ? { pct: live2pm } : null;

  return {
    districtId: path.id,
    eligibleVoters: baseline.registered,
    source,
    winner,
    marginPp,
    votedPct,
    tenPm,
    twoPm,
    baseline2022Pct: baselinePct,
    live10pmPct: live10pm,
    live2pmPct: live2pm,
  };
}

// ── Info panel (preserved from previous implementation) ──────────

interface InfoPanelProps {
  reading: TileReading;
  editorial: DistrictEditorialResult | undefined;
  onClose: () => void;
}

function InfoPanel({ reading, editorial, onClose }: InfoPanelProps) {
  const swing =
    reading.live10pmPct !== null
      ? reading.live10pmPct - reading.baseline2022Pct
      : null;
  return (
    <aside className="electionday-turnout-tiles__info" aria-live="polite">
      <div className="electionday-turnout-tiles__info-head">
        <strong>District {reading.districtId}</strong>
        <button
          type="button"
          className="electionday-turnout-tiles__info-close"
          onClick={onClose}
          aria-label="Close info panel"
        >
          ×
        </button>
      </div>
      <dl className="electionday-turnout-tiles__info-grid">
        <dt>Eligible voters</dt>
        <dd>{reading.eligibleVoters.toLocaleString()}</dd>

        <dt>2022 closing turnout</dt>
        <dd>{reading.baseline2022Pct.toFixed(1)}%</dd>

        <dt>2026 · 2pm</dt>
        <dd>
          {reading.live2pmPct !== null
            ? `${reading.live2pmPct.toFixed(1)}%`
            : '— (not entered)'}
        </dd>

        <dt>2026 · 10pm</dt>
        <dd>
          {reading.live10pmPct !== null
            ? `${reading.live10pmPct.toFixed(1)}%`
            : '— (not entered)'}
        </dd>

        <dt>Swing vs 2022</dt>
        <dd>
          {swing === null ? (
            <span className="electionday-turnout-tiles__info-muted">
              — (need 2026 10pm reading)
            </span>
          ) : (
            <span
              className={`electionday-turnout-tiles__info-swing electionday-turnout-tiles__info-swing--${
                swing > 0 ? 'up' : swing < 0 ? 'down' : 'flat'
              }`}
            >
              {swing > 0 ? '+' : ''}
              {swing.toFixed(1)}pp
            </span>
          )}
        </dd>

        {editorial?.note && (
          <>
            <dt>Editorial note</dt>
            <dd>{editorial.note}</dd>
          </>
        )}
      </dl>
    </aside>
  );
}
